import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { StorageService } from 'app/modules/shared/services/storage.service';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { TabViewModule } from 'primeng/tabview';
import { ToastModule } from 'primeng/toast';
import { AppSettings } from '../../../shared/app.settings';
import { StatusComponent } from '../../../shared/components/status/status.component';
import type { Country } from '../../../shared/models/country';
import type { Status } from '../../../shared/models/status';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';
import { SponsorOverviewDetailsComponent } from './overview-details/overview-details.component';
import { ProtocolDetailsComponent } from './protocol-details/protocol-details.component';
import { UserDetailsComponent } from './user-details/user-details.component';
import { CardModule } from 'primeng/card';
import { EntityResponse, RelatedDatum } from '../../../shared/models/entityResponse.models';
import { AttributeData } from '../../../shared/models/attribute.models';

interface DriverInfo {
  driverProfileImage: string;
  driverProfileImageId: string;
  driverName: string;
}

interface SponsorRelatedData {
  sponsor: Array<Record<string, unknown>>;
}

interface DashboardCount {
  [key: string]: number;
}

@Component({
  selector: 'app-sponsor-details',
  standalone: true,
  imports: [
    BreadcrumbModule,
    StatusComponent,
    TabViewModule,
    TranslateModule,
    MenuModule,
    ToastModule,
    SponsorOverviewDetailsComponent,
    ButtonModule,
    AccessProviderDirective,
    ProtocolDetailsComponent,
    UserDetailsComponent,
    CardModule
  ],
  templateUrl: './sponsor-details.component.html',
  styleUrl: './sponsor-details.component.scss'
})

export class SponsorDetailsComponent implements OnInit {
  @Input() tableData: Array<Record<string, unknown>>;
  routePath: MenuItem[] = [];
  sponsorId: string;
  dataForVehicleStatus: Array<Status> = [];
  userId: string = '';
  country: Country;
  activeIndex: number = 0;
  data: AttributeData;
  sponsorAttributeData: AttributeData;
  attributeLabels: Record<string, string> = {};
  vehicleAttributeLength: number;
  attributeValues: Record<string, unknown> = {};
  imgFileId: string;
  imgFileUrl: string;
  showStatus: boolean = false;
  vehicleEntityId: string;
  currentRow: Record<string, unknown>;
  sponsorDetails: EntityResponse;
  entityForStatus: Record<string, unknown>;
  editBtnLabel: string;
  relatedData: Array<RelatedDatum>;
  driverList: DriverInfo[] = [];
  defaultLoaderImg = AppSettings.DEFAULT_LOADER_IMAGE;
  entityCode: string = AppSettings.ENTITY_CODE.VEHICLE_DOCUMENT;
  isUnassigned: boolean = false;
  isDeleted: boolean = false;
  breadCrumbLabel: string;
  dashboardCount: DashboardCount;
  sponsorRelatedData: SponsorRelatedData;

  constructor(private route: ActivatedRoute,
    public cs: CommonBindingDataService,
    private cd: ChangeDetectorRef,
    private sponsorService: EntityService,
    private storageService: StorageService,
    private router: Router) {

  }

  ngOnInit() {
    this.editBtnLabel = this.cs.getLabel('lbl_edit');
    this.route.queryParams.subscribe(params => {
      this.sponsorId = params?.id;
      switch (params?.tab) {
        case 'overview':
          this.breadCrumbLabel = 'Overview';
          this.activeIndex = 0;
          break;
        case 'protocols':
          this.breadCrumbLabel = 'Protocols';
          this.activeIndex = 1;
          break;
        case 'users':
          this.breadCrumbLabel = 'Users';
          this.activeIndex = 2;
          break;
      }
    })
    this.getSponsorDetails();
    this.getAttributeData();
    this.setRoutePath();
    this.sponsorService.entityCurrentRow.subscribe(res => {
      this.currentRow = res;
    })
    this.getDashboardCount();
  }

  setRoutePath() {
    this.routePath = [
      {
        label: "Sponsors",
        routerLink: '/app/sponsors',
        icon: 'pi pi-arrow-left',
        iconStyle: { 'font-weight': 'bold', 'margin-right': '10px' },
        command: () => {
          this.storageService.setItem('vehicleActiveIndex', 0);
          this.router.navigate['app/sponsors'];
        }
      },
      {
        label: this.breadCrumbLabel,
        styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
        style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
      },
    ];
  }

  activeIndexChange(event: number) {
    this.activeIndex = event;
    switch (event) {
      case 0:
        this.router.navigate(['app/sponsors/sponsor-details'], { queryParams: { tab: 'overview', id: this.sponsorId } });
        break;
      case 1:
        this.router.navigate(['app/sponsors/sponsor-details'], { queryParams: { tab: 'protocols', id: this.sponsorId } });
        break;
      case 2:
        this.router.navigate(['app/sponsors/sponsor-details'], { queryParams: { tab: 'users', id: this.sponsorId } });
        break;
    }
  }

  getAttributeData() {
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.sponsorService.getAttributeDefinition(AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe(res => {

      res.tabs.forEach(element => {
        element.groups.forEach(group => {
          if (group.code === 'details') {
            group.entityRelationshipConfigId = null;
          }
        });
      });
      this.data = res;
      this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as Record<string, string>;
      this.sponsorAttributeData = this.cs.getOrganizedAttribute(this.data) as unknown as AttributeData;
      this.vehicleAttributeLength = this.sponsorAttributeData.tabs.length;
      this.cd.detectChanges();
    })
  }

  getSponsorDetails() {
    this.sponsorService.getEntity(this.sponsorId, AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.DETAIL_VIEW).subscribe((result: EntityResponse) => {
      this.attributeValues = this.cs.getOrgAttributeValues(result) as unknown as Record<string, unknown>;
      this.sponsorDetails = result;
      this.isDeleted = this.getDeleted();
      this.isUnassigned = this.getUnassigned();
      const value = result.attributeCodeValueDtoList.find(ele => ele.attributeCode === 'vehicle_images_url');
      this.driverList = result.relatedData.filter(ele => ele.entityCode === 'driver').map(ele => {
        return {
          driverProfileImage: ele.attributeCodeValueDtoList.find(ele => ele.attributeCode === 'driver_profile_image_url')?.attributeValue[0] as string,
          driverProfileImageId: ele.attributeCodeValueDtoList.find(ele => ele.attributeCode === 'driver_profile_image')?.attributeValue[0] as string,
          driverName: ` ${ele.attributeCodeValueDtoList.find(ele => ele.attributeCode === 'first_name').attributeValue} ${ele.attributeCodeValueDtoList.find(ele => ele.attributeCode === 'last_name').attributeValue}`
        }
      });

      if (value) {
        this.imgFileUrl = value.attributeValue[0] as string;
        this.cd.detectChanges();
      }
      this.showStatus = true;
      this.setStatusData();
      this.processRelatedData(this.attributeValues);
    })
  }

  private processRelatedData(data: Record<string, unknown>) {
    data.entityId = this.sponsorId;
    data.entity_name = data.sponsor_name;
    this.sponsorRelatedData = { sponsor: [] };
    this.sponsorRelatedData.sponsor.push(data);
  }

  setStatusData() {
    const findAttributeValue = (attributeCode: string) =>
      this.sponsorDetails?.attributeCodeValueDtoList.find(ele => ele.attributeCode === attributeCode)?.attributeValue;

    const sponsorCodeObj = { ID: findAttributeValue('sponsor_code') };
    const sponsorStatusValue = findAttributeValue('sponsor_status');
    const sponsorStatus = typeof sponsorStatusValue === 'object' && sponsorStatusValue !== null && !Array.isArray(sponsorStatusValue) ? sponsorStatusValue as Record<string, unknown> : {};
    this.entityForStatus = {
      entityName: `${findAttributeValue('sponsor_name')}`,
      entityStatus: { ...sponsorCodeObj, ...sponsorStatus }
    };
  }

  edit() {
    this.router.navigate([`app/sponsors/add-sponsor/${this.sponsorDetails.entityId}`]);
  }

  getDeleted(): boolean {
    return this.sponsorDetails?.actionStatus === 'DELETED';
  }

  getUnassigned(): boolean {
    return this.sponsorDetails?.actionStatus === 'UNASSIGNED';
  }

  getDashboardCount() {
    this.sponsorService.getEntityDashboardCount(AppSettings.ENTITY_CODE.SPONSOR, this.sponsorId).subscribe(res => {
      this.dashboardCount = { ...res } as DashboardCount;
      this.cd.detectChanges();
    })
  }

  get protocolCount(): number {
    return this.dashboardCount?.protocol_count || 0;
  }

  get userCount(): number {
    return this.dashboardCount?.user_count || 0;
  }

  onEntityAdded() {
    this.getSponsorDetails();
    this.getDashboardCount();
    setTimeout(() => {
      this.cd.detectChanges();
    }, 100);
  }
}
